
import { useSelector, useDispatch } from "react-redux"; 
import { useParams } from "react-router-dom";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { IconButton } from "@chakra-ui/react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Flex,
  Input,
  Button,
  useBreakpointValue,
  Spinner,
  Icon
} from "@chakra-ui/react";
import { fetchChatbotResponse, fetchSectorSummary } from "../../actions/user/userActions";  

const Chatbot = ({ sectorId, observations }) => {
  const [messages, setMessages] = useState([
    { text: "Hello, how can I help you today?", sender: "bot" },
  ]);
  const [input, setInput] = useState("");

  const chatEndRef = useRef(null);
  const dispatch = useDispatch();

  const { data: userData } = useSelector((state) => state.userReducer);
  
  const {
    loading: chatLoading,
    response: botResponse,
    originalMessage,
    error: chatError,
    message: chatMsg,  
  } = useSelector((state) => state.smechatbotReducer);

  // Get sector data and sector summary
  const { data: sectors } = useSelector((state) => state.sectorSmmReducer);
  const { data: sectorSummaryData, loading: loadingSectorSummary } = useSelector((state) => state.sectorSummaryReducer);

  // Get sector name from the first item in sectors array
  const sectorName = sectors && sectors.length > 0 
    ? sectors[0]?.linkedSIC2?.name || "Sector Name Not Available"
    : "Loading sectors...";

  // Extract sector summary data
  const sectorSummary = sectorSummaryData?.data || sectorSummaryData;
  const topline = sectorSummary?.topline || "No summary available";
  const bottomline = sectorSummary?.bottomline || "";
  const financingSummary = sectorSummary?.financing || "";
  const keyObservation = sectorSummary?.key_observation || "";

  const chatWidth = useBreakpointValue({ base: "100%", md: "404px" });
  const chatHeight = useBreakpointValue({ base: "100vh", md: "490px" });
  const position = useBreakpointValue({ base: "fixed", md: "absolute" });
  const right = useBreakpointValue({ base: 0, md: "5px" });
  const bottom = useBreakpointValue({ base: 0, md: "auto" });
  const maxW = useBreakpointValue({ base: "90%", md: "80%" });
  const buttonSize = useBreakpointValue({ base: "xs", md: "sm" });
  const buttonWidth = useBreakpointValue({ base: "48%", md: "215px" });
  const secondButtonWidth = useBreakpointValue({ base: "48%", md: "192px" });
  const inputPadding = useBreakpointValue({ base: 2, md: 4 });

  // Fetch sector summary when sector name is available
  useEffect(() => {
    if (sectorName && sectorName !== "Loading sectors..." && sectorName !== "Sector Name Not Available") {
      console.log("Fetching sector summary for:", sectorName);
      dispatch(fetchSectorSummary(sectorName));
    }
  }, [sectorName, dispatch]);

  const formatObservation = (obs, label) => {
    if (!obs) return null;
    
    return (
      <Flex 
        direction="row" 
        align="flex-start" 
        mt={2} 
        p={2}
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        {/* Left side */}
        <Box width="30%" pr={2}>
          <Text 
            fontSize={{ base: "xs", md: "sm" }} 
            fontWeight="bold"
            color="gray.700"
          >
            {label}
          </Text>
        </Box>
        
        {/* Right side  */}
        <Box width="70%" borderLeft="1px solid" pl={3} borderColor="gray.200">
          <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
            In the last 3 years, this metric has {obs.trend} in {sectorName} due to market conditions.
          </Text>
          <Text 
            fontSize={{ base: "xs", md: "sm" }} 
            mt={1}
          >
            Overall, it has {obs.direction === 'up' ? 'improved' : 'decreased'} by {obs.percentage}%
          </Text>
        </Box>
      </Flex>
    );
  };

  const handleSend = useCallback(async () => {
    if (!input.trim()) return;

    const userMessage = { text: input, sender: "user" };
    setInput("");
    setMessages((prev) => [...prev, userMessage]);

    dispatch(fetchChatbotResponse(input, sectorId));
  }, [input, sectorId, dispatch]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") handleSend();
    },
    [handleSend]
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!chatLoading && botResponse) {
      setMessages((prev) => [
        ...prev,
        { text: botResponse, sender: "bot" },
      ]);
    }
  }, [chatLoading, botResponse]);

  useEffect(() => {
    if (chatError) {
      console.error("Chatbot error:", chatError);
      setMessages((prev) => [
        ...prev,
        { text: "Sorry, I couldn't process your request. Please try again.", sender: "bot" },
      ]);
    }
  }, [chatError]);

  const { isAuthenticatedUser } = useSelector((state) => state.userReducer);

  const LightningIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
      <path
        d="M13 2 L3 14 H11 L9 22 L21 10 H13 Z"
        fill="#30D5C8"
        stroke="white"
        strokeWidth="2"
      />
    </Icon>
  );

  return (
    <Box
      position={position}
      right={right}
      bottom={bottom}
      width={chatWidth}
      height={chatHeight}
      maxW={chatWidth}
      p={0}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      bg="white"
      top={0}
      boxShadow="lg"
      borderTopRadius={{ base: "lg", md: "none" }}
      borderBottomRadius="lg"
    >
      {/* Header with summary */}
      <Box position="sticky" top="0" bg="#F8F8F8" zIndex="1" p={2} boxShadow="sm" overflow="hidden" maxH="325px">
        <Flex align="center" gap={3}>
          <ChatbotIcon boxSize={{ base: "32px", md: "40px", lg: "48px" }} />
          <Text fontWeight="bold" mb={2} color={"#30D5C8"}>
            Hello {userData?.full_name || "User"}, 
          </Text>
        </Flex>
        
        <Box
          fontSize={{ base: "xs", md: "12px" }}
          color="gray.800"
          sx={{
            filter: !isAuthenticatedUser ? "blur(6px)" : "none",
            pointerEvents: !isAuthenticatedUser ? "none" : "auto",
          }}
        >
          <Text fontWeight="bold" mb={1}>Summary:</Text>

          {loadingSectorSummary ? (
            <Flex justify="center" align="center" p={2}>
              <Spinner size="sm" color="#30D5C8" />
              <Text ml={2} fontSize="xs">Loading sector summary...</Text>
            </Flex>
          ) : (
            <>
              <Box pl={4} mb={1}>
                <Text>• Topline:</Text>
                <Text fontSize={{ base: "xs", md: "sm" }}>
                  {topline}
                </Text>
              </Box>

              {bottomline && (
                <Box pl={4} mb={1}>
                  <Text>• Bottomline:</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }}>
                    {bottomline}
                  </Text>
                </Box>
              )}

              {financingSummary && (
                <Box pl={4} mb={1}>
                  <Text>• Financing:</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }}>
                    {financingSummary}
                  </Text>
                </Box>
              )}

              {keyObservation && (
                <Box pl={4} mb={1}>
                  <Text>• Key Observation:</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }}>
                    {keyObservation}
                  </Text>
                </Box>
              )}

              {/* Pandemic note from the image */}
              {financingSummary && (
                <Flex align="center" bg={"#30D5C8"} p={2} borderRadius="md" mt={2}>
                  <LightningIcon boxSize={4} mr={2} />
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    fontStyle="italic"
                    color="white"
                  >
                    Pandemic dip during 2020 - 2022, now recovering via exports and D2C.
                    <Box textAlign="right" mt={1} color="white">
                      Times of India
                    </Box>
                  </Text>
                </Flex>
              )}
            </>
          )}
        </Box>
      </Box>

      <VStack spacing={3} align="stretch" overflowY="hidden" flex="1" p={2}>
        {messages.map((msg, index) => (
          <Box
            key={`${index}-${msg.text?.substring(0, 10)}`}
            alignSelf={msg.sender === "user" ? "flex-end" : "flex-start"}
            bg={msg.sender === "user" ? "blue.500" : "gray.200"}
            color={msg.sender === "user" ? "white" : "black"}
            px={4}
            py={2}
            borderRadius="md"
            maxW={maxW}
            fontSize={{ base: "sm", md: "md" }}
          >
            <Text>{msg.text}</Text>
          </Box>
        ))}

        {chatLoading && (
          <Box alignSelf="flex-start" bg="gray.200" px={4} py={2} borderRadius="md" maxW={maxW}>
            <Text>Thinking...</Text>
          </Box>
        )}
        <div ref={chatEndRef} />
      </VStack>

      <HStack justifyContent="space-between">
        <Button 
          size={buttonSize} 
          fontWeight={500} 
          w={buttonWidth} 
          bg="#FFAA00" 
          color="#162421" 
          fontSize={{ base: "xs", md: "12px" }}
          _hover={{ bg: "#30D5C8" }} 
          onClick={() => setInput("Generate competitive analysis")}
        >
          Generate competitive analysis
        </Button>

        <Button 
          size={buttonSize} 
          w={secondButtonWidth} 
          bg="#FFAA00" 
          color="#162421" 
          fontSize={{ base: "xs", md: "12px" }}
          fontWeight={500} 
          _hover={{ bg: "#30D5C8" }} 
          onClick={() => setInput("Analyze team performance")}
        >
          Analyze team performance
        </Button>
      </HStack>

      <Box p={2} bg="white" borderTopWidth="1px" borderTopColor="gray.200">
        <HStack mb={2}>
          <Input
            placeholder="Type your query here..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={chatLoading}
            size={buttonSize}
            px={inputPadding}
          />
          <IconButton
            aria-label="Send"
            icon={<PaperAirplaneIcon style={{ width: "1.25rem", height: "1.25rem" }} />}
            bg="teal.400"
            _hover={{ bg: "teal.500" }}
            color="black"
            rounded="md"
            onClick={handleSend}
            isLoading={chatLoading}
            disabled={!input.trim()}
          />
        </HStack>
      </Box>
    </Box>
  );
};

export default React.memo(Chatbot);


 export function PaperAirplaneIcon(props) {
  return (
    <Icon viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M3.404 2.293a.75.75 0 01.82-.166l17.25 7.25a.75.75 0 010 1.366l-17.25 7.25a.75.75 0 01-1.037-.886l2.48-7.723a.75.75 0 010-.474l-2.48-7.723a.75.75 0 01.187-.694zM6.14 9.412l1.65 5.137a.75.75 0 00.474.474l5.137 1.65a.75.75 0 00.886-1.037L12.037 12l2.25-3.636a.75.75 0 00-1.037-.886L8.113 9.125a.75.75 0 00-.474.474L6.14 9.412z"
      />
    </Icon>
  );
}
const ChatbotIcon = (props) => (
  <Icon viewBox="0 0 64 64" {...props}>
    <defs>
      <linearGradient id="chatbotGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#30D5C8" />
        <stop offset="100%" stopColor="#FFD54F" />
      </linearGradient>
    </defs>
    <rect x="8" y="20" width="48" height="36" rx="12" fill="url(#chatbotGradient)" />
    <rect x="30" y="8" width="4" height="12" fill="url(#chatbotGradient)" />
    <circle cx="32" cy="6" r="6" fill="url(#chatbotGradient)" />
    <circle cx="24" cy="34" r="3" fill="white" />
    <circle cx="40" cy="34" r="3" fill="white" />
    <path
      d="M22 42 C26 48, 38 48, 42 42"
      stroke="white"
      strokeWidth="3"
      fill="transparent"
      strokeLinecap="round"
    />
  </Icon>
);